'use client';

import React, { useCallback } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { X, ImageUp, MapPin } from 'lucide-react';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import LocationPickerMap from '@/components/LocationPickerMap';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { NeighborhoodSelect } from '@/components/ui/neighborhood-select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadTrigger,
} from '@/components/ui/file-upload';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { INPUT_LIMITS } from '@/constants/inputLimits';
import { Checkbox } from '@/components/ui';
import { PhoneInput } from '@/components/phone-input';

interface SelectedLocation {
  lat: number;
  lng: number;
  formattedAddress?: string;
  country?: string;
  city?: string;
  street?: string;
}

interface OverviewStepProps {
  control: Control<any>;
  thumbnailFiles: File[];
  coverFiles: File[];
  onThumbnailChange: (files: File[]) => void;
  onCoverChange: (files: File[]) => void;
  categoriesData: any;
  existingThumbnailImage?: string;
  existingCoverImage?: string;
  isEdit: boolean;
  showThumbnailImageErrors: boolean;
  showCoverImageErrors: boolean;
  selectedNeighborhoodName: string;
  setSelectedNeighborhoodName: (name: string) => void;
  selectedLocation: SelectedLocation | null;
  setSelectedLocation: (location: SelectedLocation | null) => void;
  showLocationPicker: boolean;
  setShowLocationPicker: (show: boolean) => void;
}

export default function OverviewStep({
  control,
  thumbnailFiles,
  coverFiles,
  onThumbnailChange,
  onCoverChange,
  categoriesData,
  existingThumbnailImage,
  existingCoverImage,
  showThumbnailImageErrors,
  showCoverImageErrors,
  selectedNeighborhoodName,
  setSelectedNeighborhoodName,
  selectedLocation,
  setSelectedLocation,
  showLocationPicker,
  setShowLocationPicker,
}: OverviewStepProps) {
  const form = useFormContext();

  const onThumbnailFileReject = useCallback((file: File, message: string) => {
    toast.error(message || 'File upload failed');
  }, []);

  const onCoverFileReject = useCallback((file: File, message: string) => {
    toast.error(message || 'File upload failed');
  }, []);

  return (
    <div className="space-y-1">
      <FormField
        control={control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="xl:text-sm ml-1">School Name</FormLabel>
            <FormControl>
              <Input
                className="border-1 placeholder:text-muted-foreground placeholder:text-sm text-sm"
                {...field}
                placeholder="Enter school name"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="overview"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="xl:text-sm ml-1">Overview</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                placeholder="Enter overview"
                rows={6}
                className="max-w-full min-h-[100px] overflow-y-auto resize-none placeholder:text-muted-foreground placeholder:text-sm"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex flex-col sm:flex-row gap-4 w-full">
        <FormField
          control={control}
          name="categoryID"
          render={({ field }) => {
            return (
              <FormItem className="flex-1 min-w-0">
                <FormLabel className="xl:text-sm  ml-1 mb-1">
                  Category
                </FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(value) => field.onChange(value)}
                >
                  <FormControl>
                    <SelectTrigger
                      className={`w-full text-sm border-1 rounded-full px-2 py-1 border-[#cdc4c1] text-black h-8`}
                    >
                      <SelectValue placeholder="Select Category">
                        {field.value &&
                          categoriesData?.responseObject?.rows
                            ?.find((opt: any) => opt.id === field.value)
                            ?.name?.replace(/\b\w/g, (char: string) =>
                              char.toUpperCase()
                            )}
                      </SelectValue>
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      {categoriesData?.responseObject?.rows?.map(
                        (opt: any) => (
                          <SelectItem key={opt.id} value={opt.id}>
                            <div className="flex flex-col w-full items-start">
                              <span className="font-medium">
                                {opt.name.replace(/\b\w/g, (char: string) =>
                                  char.toUpperCase()
                                )}
                              </span>
                            </div>
                          </SelectItem>
                        )
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <div className="h-5">
                  <FormMessage />
                </div>
              </FormItem>
            );
          }}
        />

        <div className="flex-1 min-w-0 flex items-start gap-2">
          <NeighborhoodSelect
            form={form}
            name="neighborhoodID"
            label="Neighborhood"
            placeholder="Select Neighborhood"
            selectedName={selectedNeighborhoodName}
            onSelectedNameChange={setSelectedNeighborhoodName}
            labelClassName="mb-1"
          />
          <div className="flex flex-col flex-shrink-0">
            {/* Invisible label spacer so the button sits at the same y as
                the neighborhood select trigger. */}
            <FormLabel className="xl:text-sm ml-1 mb-1 invisible select-none">
              .
            </FormLabel>
            <button
              type="button"
              onClick={() => setShowLocationPicker(!showLocationPicker)}
              className={`h-9 w-9 flex items-center justify-center mt-2.5 rounded-full transition-colors ${
                selectedLocation
                  ? 'bg-[#6b5d52] text-[#eae7e0] hover:bg-[#6b5d52]'
                  : 'bg-[#948780] text-[#eae7e0] hover:bg-[#948780]'
              }`}
              title={selectedLocation ? 'Edit location' : 'Add location'}
            >
              <MapPin className="h-4 w-4" strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>

      {showLocationPicker && (
        <div className="mt-3 pt-3 border-t">
          <LocationPickerMap
            onLocationSelect={(location) => {
              setSelectedLocation(location);
            }}
            onClose={() => setShowLocationPicker(false)}
            initialLocation={selectedLocation || undefined}
            showControls={true}
          />
        </div>
      )}
      <div className="flex gap-2">
        <FormField
          control={control}
          name="agesEnrolled"
          render={({ field }) => (
            <FormItem className="flex-[5] -mr-2">
              <FormLabel className="xl:text-sm ml-1">Ages Enrolled</FormLabel>
              <FormControl>
                <Input
                  className="placeholder:text-muted-foreground placeholder:text-sm"
                  {...field}
                  placeholder="6 weeks to 5 years"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="partOrFullTime"
          render={({ field }) => {
            const values: string[] = field.value || [];
            return (
              <FormItem className="flex-[4]">
                <FormControl>
                  <div className="flex gap-6 items-center ml-4 mt-7">
                    {/* Part Time */}
                    <div className="flex items-center gap-2 text-md">
                      <Checkbox
                        className="rounded-sm xl:text-lg text-sm"
                        checked={values.includes('Part')}
                        onCheckedChange={(checked) => {
                          let newValue = values;

                          if (checked) {
                            newValue = [...values, 'Part'];
                          } else {
                            newValue = values.filter((v) => v !== 'Part');
                          }

                          field.onChange(newValue);
                        }}
                      />
                      <span className="xl:text-lg text-sm">Part Time</span>
                    </div>

                    {/* Full Time */}
                    <div className="flex items-center gap-2">
                      <Checkbox
                        className="rounded-sm xl:text-lg text-sm"
                        checked={values.includes('Full')}
                        onCheckedChange={(checked) => {
                          let newValue = values;

                          if (checked) {
                            newValue = [...values, 'Full'];
                          } else {
                            newValue = values.filter((v) => v !== 'Full');
                          }

                          field.onChange(newValue);
                        }}
                      />
                      <span className="text-sm xl:text-lg text-sm">
                        Full Time
                      </span>
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </div>
      <div className="flex gap-4 w-full">
      <FormField
        control={control}
        name="website"
        render={({ field }) => (
          <FormItem className="flex-[2]">
            <FormLabel className="xl:text-sm ml-1">Website</FormLabel>
            <FormControl>
              <Input
                className="border-1 placeholder:text-muted-foreground placeholder:text-sm"
                {...field}
                placeholder="Website url"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
            <FormField
        control={control}
        name="email"
        render={({ field }) => (
          <FormItem className="flex-[2]">
            <FormLabel className="xl:text-sm ml-1">Email</FormLabel>
            <FormControl>
              <Input
                className="border-1 placeholder:text-muted-foreground placeholder:text-sm"
                {...field}
                placeholder="Email address"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
       
      </div>
      <div className="flex gap-4 w-full">

        <FormField
        control={control}
        name="phone"
        render={({ field }) => (
          <FormItem className="flex-[2]">
            <FormLabel className="xl:text-sm ml-1">Phone</FormLabel>
            <FormControl>
            <PhoneInput
                   inputClassName=" placeholder:text-muted-foreground placeholder:text-sm"
                   className="placeholder:text-muted-foreground placeholder:text-sm"
                   placeholder="Phone number"
                   {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
       <FormField
        control={control}
        name="workHours"
        render={({ field }) => (
          <FormItem className="flex-[2]">
            <FormLabel className="xl:text-sm ml-1">Work Hours</FormLabel>
            <FormControl>
              <Input
                className="border-1 placeholder:text-muted-foreground placeholder:text-sm"
                {...field}
                placeholder="8:00AM - 5:00PM"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      </div>

      {selectedLocation && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <div className="flex items-center gap-1 px-2 py-1 bg-muted/30 rounded-md border border-border/50 max-w-[260px]">
            <span className="text-xs text-muted-foreground truncate">
              {selectedLocation.formattedAddress ||
                `${selectedLocation.lat.toFixed(4)}, ${selectedLocation.lng.toFixed(4)}`}
            </span>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedLocation(null);
            }}
            className="h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs hover:bg-red-600 flex-shrink-0"
            title="Remove location"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Image Upload Section - Modern Design */}
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-4">
          {/* Thumbnail Upload Card */}
          <div>
            <FormField
              control={control}
              name="thumbnailImage"
              render={({ field }) => (
                <div>
                  <FormLabel
                    className={
                      showThumbnailImageErrors &&
                      !(
                        field.value === 'existing' ||
                        field.value === 'new' ||
                        thumbnailFiles.length > 0 ||
                        (existingThumbnailImage &&
                          existingThumbnailImage.trim() !== '')
                      )
                        ? 'text-destructive'
                        : 'text-black'
                    }
                  >
                    Thumbnail Image
                  </FormLabel>
                  <FormControl>
                    <FileUpload
                      maxFiles={INPUT_LIMITS.PROFILE.AVATAR_MAX_COUNT}
                      maxSize={INPUT_LIMITS.IMAGES.MAX_SIZE_BYTES}
                      accept={INPUT_LIMITS.IMAGES.SUPPORTED_EXTENSIONS.map(
                        (ext) => `.${ext}`
                      ).join(',')}
                      value={field.value === 'existing' ? [] : thumbnailFiles}
                      onValueChange={(files) => {
                        onThumbnailChange(files);
                        // Only update form field if we're not in the middle of a form reset
                        if (files.length > 0) {
                          field.onChange('new');
                        } else if (
                          field.value !== 'existing' &&
                          field.value !== ''
                        ) {
                          field.onChange('');
                        }
                      }}
                      onFileReject={onThumbnailFileReject}
                    >
                      <FileUploadDropzone className="border-2 border-dashed rounded-lg hover:border-gray-400 transition-colors">
                        {thumbnailFiles.length > 0 ? (
                          <div className="relative group h-32 w-full">
                            <Image
                              src={URL.createObjectURL(thumbnailFiles[0])}
                              alt="Thumbnail"
                              fill
                              className="object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onThumbnailChange([]);
                                  form.setValue('thumbnailImage', '');
                                }}
                                className="rounded-full"
                              >
                                <X className="h-4 w-4 mr-1" />
                                Remove
                              </Button>
                            </div>
                          </div>
                        ) : existingThumbnailImage ? (
                          <div className="relative group h-32 w-full">
                            <FileUploadTrigger asChild>
                              <div className="absolute inset-0 cursor-pointer">
                                <Image
                                  src={existingThumbnailImage}
                                  alt="Thumbnail"
                                  fill
                                  className="object-cover rounded-lg cursor-pointer"
                                />
                              </div>
                            </FileUploadTrigger>
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center pointer-events-none">
                              <div className="pointer-events-auto">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="rounded-full bg-white text-black hover:bg-gray-100"
                                >
                                  <ImageUp className="h-4 w-4 mr-1" />
                                  Replace
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <FileUploadTrigger className="w-full">
                            <div className="flex flex-col items-center justify-center h-32 cursor-pointer">
                              <ImageUp className="h-8 w-8 text-gray-400 mb-2" />
                              <p className="text-sm font-medium text-gray-700">
                                Upload Thumbnail
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                PNG, JPG up to 5MB
                              </p>
                            </div>
                          </FileUploadTrigger>
                        )}
                      </FileUploadDropzone>
                    </FileUpload>
                  </FormControl>
                  {showThumbnailImageErrors &&
                    !(
                      field.value === 'existing' ||
                      field.value === 'new' ||
                      thumbnailFiles.length > 0 ||
                      (existingThumbnailImage &&
                        existingThumbnailImage.trim() !== '')
                    ) && (
                      <p className="text-sm font-medium text-destructive">
                        Thumbnail image is required
                      </p>
                    )}
                </div>
              )}
            />
          </div>

          {/* Cover Upload Card */}
          <div>
            <FormField
              control={control}
              name="coverImage"
              render={({ field }) => {
                const isValid =
                  field.value === 'existing' ||
                  field.value === 'new' ||
                  coverFiles.length > 0 ||
                  (existingCoverImage && existingCoverImage.trim() !== '');
                return (
                  <div>
                    <FormLabel
                      key={`cover-label-${field.value}`}
                      className={
                        !isValid && showCoverImageErrors
                          ? 'text-destructive'
                          : 'text-black'
                      }
                    >
                      Cover Image
                    </FormLabel>
                    <FormControl>
                      <FileUpload
                        maxFiles={INPUT_LIMITS.PROFILE.COVER_MAX_COUNT}
                        maxSize={INPUT_LIMITS.IMAGES.MAX_SIZE_BYTES}
                        accept={INPUT_LIMITS.IMAGES.SUPPORTED_EXTENSIONS.map(
                          (ext) => `.${ext}`
                        ).join(',')}
                        value={field.value === 'existing' ? [] : coverFiles}
                        onValueChange={(files) => {
                          onCoverChange(files);
                          // Only update form field if we're not in the middle of a form reset
                          if (files.length > 0) {
                            field.onChange('new');
                          } else if (
                            field.value !== 'existing' &&
                            field.value !== ''
                          ) {
                            field.onChange('');
                          }
                        }}
                        onFileReject={onCoverFileReject}
                      >
                        <FileUploadDropzone className="border-2 border-dashed rounded-lg hover:border-gray-400 transition-colors">
                          {coverFiles.length > 0 ? (
                            <div className="relative group h-32 w-full">
                              <Image
                                src={URL.createObjectURL(coverFiles[0])}
                                alt="Cover"
                                fill
                                className="object-cover rounded-lg"
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onCoverChange([]);
                                    form.setValue('coverImage', '');
                                  }}
                                  className="rounded-full"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ) : existingCoverImage ? (
                            <div className="relative group h-32 w-full">
                              <FileUploadTrigger asChild>
                                <div className="absolute inset-0 cursor-pointer">
                                  <Image
                                    src={existingCoverImage}
                                    alt="Cover"
                                    fill
                                    className="object-cover rounded-lg cursor-pointer"
                                  />
                                </div>
                              </FileUploadTrigger>
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center pointer-events-none">
                                <div className="pointer-events-auto">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full bg-white text-black hover:bg-gray-100"
                                  >
                                    <ImageUp className="h-4 w-4 mr-1" />
                                    Replace
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <FileUploadTrigger className="w-full">
                              <div className="flex flex-col items-center justify-center h-32 cursor-pointer">
                                <ImageUp className="h-8 w-8 text-gray-400 mb-2" />
                                <p className="text-sm font-medium text-gray-700">
                                  Upload Cover
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  PNG, JPG up to 5MB
                                </p>
                              </div>
                            </FileUploadTrigger>
                          )}
                        </FileUploadDropzone>
                      </FileUpload>
                    </FormControl>
                    {showCoverImageErrors &&
                      !(
                        field.value === 'existing' ||
                        field.value === 'new' ||
                        coverFiles.length > 0 ||
                        (existingCoverImage && existingCoverImage.trim() !== '')
                      ) && (
                        <p className="text-sm font-medium text-destructive">
                          Cover image is required
                        </p>
                      )}
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
